// Historial de evaluaciones académicas (CAD) de un estudiante.
// Se abre desde "Diagnósticos académicos" → botón "Historial".
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, UserRound } from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return "—"; }
};
const scoreColor = (v) => (v == null ? "text-[#A6ACC1]" : v >= 70 ? "text-[#2A7A36]" : v >= 40 ? "text-[#9C7A18]" : "text-[#A52258]");

export default function HistorialAcademico() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/users/${id}`).then(({ data }) => { if (alive) setStudent(data); }).catch(() => {});
    api.get(`/students/${id}/evaluations`, { params: { kind: "academic", page_size: 50 } })
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => { if (alive) { setItems([]); setError("No se pudo cargar el historial de este estudiante."); } });
    return () => { alive = false; };
  }, [id]);

  const areas = Array.from(new Set((items || []).flatMap((it) => Object.keys(it.by_area || {}))));

  return (
    <div className="space-y-5" data-testid="historial-academico-page">
      <Link to="/docente/resultados-academicos" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2C49A0] hover:underline" data-testid="historial-volver">
        <ArrowLeft size={14} /> Volver a diagnósticos académicos
      </Link>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#2C49A0]">Diagnósticos académicos</p>
        <h1 className="font-display text-3xl font-extrabold text-[#1F2547] inline-flex items-center gap-2">
          <UserRound size={26} className="text-[#A43790]" /> Historial · {student?.name || "Estudiante"}
        </h1>
        <p className="text-sm text-[#6E7591] mt-1">Todas las evaluaciones académicas (CAD) registradas, de la más reciente a la más antigua.</p>
      </div>

      {items === null && <p className="text-sm text-[#6E7591] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</p>}
      {error && <p className="text-sm text-[#A52258]" data-testid="historial-error">{error}</p>}
      {items && !error && items.length === 0 && (
        <div className="jasq-card p-8 text-center text-sm text-[#6E7591]" data-testid="historial-vacio">Este estudiante aún no tiene evaluaciones académicas.</div>
      )}
      {items && items.length > 0 && (
        <div className="jasq-card overflow-x-auto" data-testid="historial-tabla">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7591]">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-3 py-3 text-center">Total</th>
                {areas.map((a) => <th key={a} className="px-3 py-3 text-center">{a}</th>)}
                <th className="px-3 py-3 text-center">Respondidas</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id || i} className="border-t border-[#E1E5F0]">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-[#4C5478]">{fmtDate(it.created_at)}</td>
                  <td className={`px-3 py-3 text-center font-extrabold ${scoreColor(it.score_total)}`}>{it.score_total != null ? `${it.score_total}%` : "—"}</td>
                  {areas.map((a) => {
                    const v = it.by_area?.[a];
                    return <td key={a} className={`px-3 py-3 text-center font-bold ${scoreColor(v)}`}>{v == null ? "—" : `${v}%`}</td>;
                  })}
                  <td className="px-3 py-3 text-center text-xs text-[#6E7591]">{it.answered != null && it.total ? `${it.answered}/${it.total}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
